import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import Seo from '@/components/Seo';
import { toast, Toaster } from 'sonner';
import { Images, Upload, Download, Lock, RefreshCw, Rows, Columns, SlidersHorizontal, Sparkles, Copy, Check, Loader2, Save } from 'lucide-react';
import { knockoutWhite } from '@/lib/imageUtils';
import BAModeNav from '@/components/BAModeNav';

const PRESETS = [
  { key: 'square', label: 'Square 1:1', hint: 'Feed post', w: 1080, h: 1080 },
  { key: 'portrait', label: 'Portrait 4:5', hint: 'Feed post', w: 1080, h: 1350 },
  { key: 'story', label: 'Story 9:16', hint: 'Stories / Reels', w: 1080, h: 1920 },
  { key: 'landscape', label: 'Landscape 16:9', hint: 'Facebook', w: 1080, h: 608 },
];
const BRAND_SWATCHES = ['#FF5A1F', '#0f172a', '#2563eb', '#16a34a', '#dc2626', '#7c3aed', '#0891b2'];

async function fileToBitmap(file) {
  try { return await createImageBitmap(file, { imageOrientation: 'from-image' }); }
  catch { return await createImageBitmap(file); }
}
async function urlToBitmap(dataUrl) {
  const res = await fetch(dataUrl); const blob = await res.blob();
  return await createImageBitmap(blob);
}
function drawCover(ctx, img, x, y, w, h) {
  const ir = img.width / img.height, cr = w / h;
  let sw, sh, sx, sy;
  if (ir > cr) { sh = img.height; sw = sh * cr; sx = (img.width - sw) / 2; sy = 0; }
  else { sw = img.width; sh = sw / cr; sx = 0; sy = (img.height - sh) / 2; }
  // Progressive downscale for big source crops so the result stays sharp (avoids one-step blur).
  const dpr = Math.max(1, Math.ceil(Math.min(sw / w, sh / h)));
  if (dpr > 2) {
    let cw = Math.round(sw), ch = Math.round(sh);
    let tmp = document.createElement('canvas');
    tmp.width = cw; tmp.height = ch;
    let tctx = tmp.getContext('2d');
    tctx.imageSmoothingEnabled = true; tctx.imageSmoothingQuality = 'high';
    tctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
    while (cw > w * 2 && ch > h * 2) {
      const nw = Math.max(Math.round(w), Math.round(cw / 2));
      const nh = Math.max(Math.round(h), Math.round(ch / 2));
      const next = document.createElement('canvas');
      next.width = nw; next.height = nh;
      const nctx = next.getContext('2d');
      nctx.imageSmoothingEnabled = true; nctx.imageSmoothingQuality = 'high';
      nctx.drawImage(tmp, 0, 0, cw, ch, 0, 0, nw, nh);
      tmp = next; tctx = nctx; cw = nw; ch = nh;
    }
    ctx.drawImage(tmp, 0, 0, cw, ch, x, y, w, h);
    return;
  }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}
function pill(ctx, text, x, y, scale, accent) {
  ctx.font = `900 ${Math.round(34 * scale)}px Arial, sans-serif`;
  const padX = 22 * scale, h = 52 * scale;
  const tw = ctx.measureText(text).width;
  ctx.fillStyle = 'rgba(15,23,42,0.92)';
  ctx.fillRect(x, y, tw + padX * 2, h);
  ctx.fillStyle = accent;
  ctx.fillRect(x, y, 8 * scale, h);
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
  ctx.fillText(text, x + padX, y + h / 2 + 2 * scale);
}

export default function BeforeAfterPage() {
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.role === 'admin';
  const canvasRef = useRef(null);
  const draggingRef = useRef(false);
  const [beforeBmp, setBeforeBmp] = useState(null);
  const [afterBmp, setAfterBmp] = useState(null);
  const [logoBmp, setLogoBmp] = useState(null);
  const [logoOriginal, setLogoOriginal] = useState(null);
  const [logoTransparent, setLogoTransparent] = useState(false);
  const [logoThreshold, setLogoThreshold] = useState(238);
  const [layout, setLayout] = useState('side'); // side | stacked | slider
  const [presetKey, setPresetKey] = useState('square');
  const [showLabels, setShowLabels] = useState(true);
  const [showFooter, setShowFooter] = useState(false);
  const [showLogo, setShowLogo] = useState(false);
  const [logoPos, setLogoPos] = useState('br'); // footer | tl | tr | bl | br
  const [logoSize, setLogoSize] = useState('m'); // s | m | l
  const [footerText, setFooterText] = useState('');
  const [brand, setBrand] = useState('#FF5A1F');
  useEffect(() => { if (user?.brand_color) setBrand(user.brand_color); }, [user?.brand_color]);
  const [divider, setDivider] = useState(0.5);
  const [savingGallery, setSavingGallery] = useState(false);
  // caption helper
  const [capTrade, setCapTrade] = useState('');
  const [capDesc, setCapDesc] = useState('');
  const [capLoading, setCapLoading] = useState(false);
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState([]);
  const [copied, setCopied] = useState('');

  useEffect(() => { if (user?.business_name && !footerText) setFooterText(user.business_name); }, [user]); // eslint-disable-line
  useEffect(() => {
    api.get('/me/logo').then((r) => {
      const raw = r.data?.original || r.data?.data_url;
      if (!raw) return;
      setLogoOriginal(raw);
      setLogoTransparent(!!r.data?.transparent);
      setLogoThreshold(r.data?.threshold || 238);
    }).catch(() => {});
  }, []);

  // Build the logo bitmap from the original, applying transparency knockout when enabled.
  useEffect(() => {
    let cancelled = false;
    if (!logoOriginal) { setLogoBmp(null); return; }
    const build = async () => {
      try {
        const src = logoTransparent ? await knockoutWhite(logoOriginal, logoThreshold) : logoOriginal;
        const bmp = await urlToBitmap(src);
        if (!cancelled) setLogoBmp(bmp);
      } catch { /* ignore */ }
    };
    const t = setTimeout(build, 120);
    return () => { cancelled = true; clearTimeout(t); };
  }, [logoOriginal, logoTransparent, logoThreshold]);

  const preset = PRESETS.find((p) => p.key === presetKey);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const RES = 2; // supersample for crisp, non-pixelated exports
    const { w: baseW, h: baseH } = preset;
    const w = baseW * RES, h = baseH * RES;
    cv.width = w; cv.height = h;
    const ctx = cv.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    const scale = w / 1080;
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, w, h);

    const footerH = showFooter ? Math.round(112 * scale) : 0;
    const areaH = h - footerH;
    const gap = Math.round(10 * scale);
    const labels = ['BEFORE', 'AFTER'];

    const placeholder = (x, y, cw, ch, i) => {
      ctx.fillStyle = i === 0 ? '#1e293b' : '#334155';
      ctx.fillRect(x, y, cw, ch);
      ctx.fillStyle = '#64748b'; ctx.font = `700 ${Math.round(28 * scale)}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(`Upload ${labels[i].toLowerCase()} photo`, x + cw / 2, y + ch / 2);
      ctx.textAlign = 'left';
    };

    if (layout === 'slider') {
      if (afterBmp) drawCover(ctx, afterBmp, 0, 0, w, areaH); else placeholder(0, 0, w, areaH, 1);
      const dx = Math.round(w * divider);
      if (beforeBmp) { ctx.save(); ctx.beginPath(); ctx.rect(0, 0, dx, areaH); ctx.clip(); drawCover(ctx, beforeBmp, 0, 0, w, areaH); ctx.restore(); }
      else { ctx.save(); ctx.beginPath(); ctx.rect(0, 0, dx, areaH); ctx.clip(); placeholder(0, 0, w, areaH, 0); ctx.restore(); }
      // divider + handle
      ctx.fillStyle = '#ffffff'; ctx.fillRect(dx - 3 * scale, 0, 6 * scale, areaH);
      const cy = areaH / 2, r = 40 * scale;
      ctx.beginPath(); ctx.arc(dx, cy, r, 0, Math.PI * 2); ctx.fillStyle = '#ffffff'; ctx.fill();
      ctx.lineWidth = 5 * scale; ctx.strokeStyle = brand; ctx.stroke();
      ctx.fillStyle = brand;
      const a = 12 * scale;
      ctx.beginPath(); ctx.moveTo(dx - 16 * scale, cy); ctx.lineTo(dx - 16 * scale + a, cy - a); ctx.lineTo(dx - 16 * scale + a, cy + a); ctx.fill();
      ctx.beginPath(); ctx.moveTo(dx + 16 * scale, cy); ctx.lineTo(dx + 16 * scale - a, cy - a); ctx.lineTo(dx + 16 * scale - a, cy + a); ctx.fill();
      if (showLabels) {
        pill(ctx, 'BEFORE', Math.round(24 * scale), Math.round(24 * scale), scale, brand);
        ctx.font = `900 ${Math.round(34 * scale)}px Arial`;
        const aw = ctx.measureText('AFTER').width + 44 * scale;
        pill(ctx, 'AFTER', w - aw - Math.round(24 * scale), Math.round(24 * scale), scale, brand);
      }
    } else {
      let cells;
      if (layout === 'side') {
        const cw = (w - gap) / 2;
        cells = [{ x: 0, y: 0, w: cw, h: areaH }, { x: cw + gap, y: 0, w: cw, h: areaH }];
      } else {
        const ch = (areaH - gap) / 2;
        cells = [{ x: 0, y: 0, w, h: ch }, { x: 0, y: ch + gap, w, h: ch }];
      }
      const imgs = [beforeBmp, afterBmp];
      cells.forEach((c, i) => { if (imgs[i]) drawCover(ctx, imgs[i], c.x, c.y, c.w, c.h); else placeholder(c.x, c.y, c.w, c.h, i); });
      if (showLabels) cells.forEach((c, i) => pill(ctx, labels[i], c.x + Math.round(24 * scale), c.y + Math.round(24 * scale), scale, brand));
    }

    if (showFooter) {
      ctx.fillStyle = brand;
      ctx.fillRect(0, h - footerH, w, footerH);
      let textX = w / 2; ctx.textAlign = 'center';
      if (showLogo && logoBmp && logoPos === 'footer') {
        const lh = Math.round(footerH * 0.66), lw = lh * (logoBmp.width / logoBmp.height);
        ctx.drawImage(logoBmp, Math.round(28 * scale), h - footerH + (footerH - lh) / 2, lw, lh);
        textX = (28 * scale + lw + w) / 2;
      }
      ctx.fillStyle = '#ffffff';
      ctx.font = `900 ${Math.round(40 * scale)}px Arial`;
      ctx.textBaseline = 'middle';
      ctx.fillText((footerText || 'Your business name').toUpperCase(), textX, h - footerH / 2);
      ctx.textAlign = 'left';
    }
    // Logo as a corner watermark (when not placed in the footer)
    if (showLogo && logoBmp && !(logoPos === 'footer' && showFooter)) {
      const baseH = { s: 90, m: 130, l: 180 }[logoSize] || 130;
      const lh = Math.round(baseH * scale), lw = lh * (logoBmp.width / logoBmp.height);
      const pad = Math.round(16 * scale), m = Math.round(36 * scale);
      const pos = logoPos === 'footer' ? 'br' : logoPos;
      const x = pos === 'tl' || pos === 'bl' ? m : w - lw - m - pad * 2;
      const y = pos === 'tl' || pos === 'tr' ? m : areaH - lh - m - pad * 2;
      if (!logoTransparent) {
        ctx.fillStyle = 'rgba(255,255,255,0.92)';
        ctx.fillRect(x, y, lw + pad * 2, lh + pad * 2);
      }
      ctx.drawImage(logoBmp, x + pad, y + pad, lw, lh);
    }
  }, [beforeBmp, afterBmp, logoBmp, layout, presetKey, showLabels, showFooter, showLogo, logoPos, logoSize, footerText, brand, divider, preset, logoTransparent]);

  const onPick = (which) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    try {
      const bmp = await fileToBitmap(file);
      if (which === 'before') setBeforeBmp(bmp); else setAfterBmp(bmp);
    } catch { toast.error('Could not read that image'); }
    e.target.value = '';
  };

  const setDividerFromEvent = (e) => {
    const cv = canvasRef.current; if (!cv) return;
    const rect = cv.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    setDivider(frac);
  };
  const onCanvasDown = (e) => { if (layout !== 'slider') return; draggingRef.current = true; setDividerFromEvent(e); };
  const onCanvasMove = (e) => { if (draggingRef.current) { e.preventDefault(); setDividerFromEvent(e); } };
  const stopDrag = () => { draggingRef.current = false; };

  const download = () => {
    if (!beforeBmp || !afterBmp) { toast.error('Upload both a Before and an After photo first'); return; }
    const trigger = (url, revoke) => {
      const a = document.createElement('a');
      a.href = url; a.download = `before-after-${layout}-${presetKey}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      if (revoke) URL.revokeObjectURL(url);
      toast.success('Image downloaded — ready to post!');
    };
    try {
      canvasRef.current.toBlob((blob) => {
        if (blob) trigger(URL.createObjectURL(blob), true);
        else trigger(canvasRef.current.toDataURL('image/png'), false);
      }, 'image/png');
    } catch { trigger(canvasRef.current.toDataURL('image/png'), false); }
  };

  const canvasToBlob = () => new Promise((resolve) => {
    try { canvasRef.current.toBlob((b) => resolve(b), 'image/png'); } catch { resolve(null); }
  });

  const saveToGallery = async () => {
    if (!beforeBmp || !afterBmp) { toast.error('Upload both a Before and an After photo first'); return; }
    setSavingGallery(true);
    try {
      const blob = await canvasToBlob();
      if (!blob) throw new Error('render');
      const fd = new FormData();
      fd.append('file', blob, `before-after-${Date.now()}.png`);
      fd.append('caption', caption || '');
      fd.append('kind', 'before-after');
      await api.post('/gallery', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Saved to your gallery');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save to gallery');
    } finally { setSavingGallery(false); }
  };

  const genCaption = async () => {
    setCapLoading(true);
    try {
      const { data } = await api.post('/marketing/caption', { trade: capTrade, description: capDesc });
      setCaption(data.caption || ''); setHashtags(data.hashtags || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate a caption — try again');
    } finally { setCapLoading(false); }
  };
  const copy = async (text, key) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(''), 1600); } catch { toast.error('Copy failed'); }
  };

  if (!isPro) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="ba-locked">
        <Lock className="mx-auto text-brand mb-4" size={40} />
        <h1 className="font-display font-semibold text-3xl uppercase tracking-tight">A Pro feature</h1>
        <p className="text-slate-600 mt-2">Create scroll-stopping Before &amp; After posts for Instagram and Facebook. Start your 14-day free trial to unlock it.</p>
        <a href="/pricing" className="tm-btn inline-flex mt-5" data-testid="ba-upgrade-btn">See Pro plans</a>
      </div>
    );
  }

  const Toggle = ({ on, set, tid, disabled, children }) => (
    <button onClick={() => !disabled && set(!on)} data-testid={tid} disabled={disabled}
      className={`inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wide border px-3 py-2 transition-colors ${disabled ? 'opacity-40 cursor-not-allowed border-slate-800' : on ? 'bg-slate-900 text-white border-slate-800' : 'border-slate-300 text-slate-600'}`}>
      <span className={`w-3 h-3 rounded-full ${on ? 'bg-brand' : 'bg-slate-300'}`} />{children}
    </button>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="before-after-page">
      <Seo title="Before & After Maker" noindex />
      <Toaster position="top-center" richColors />

      <header className="mb-6">
        <p className="text-xs uppercase tracking-widest text-brand font-semibold">Business · Marketing</p>
        <h1 className="font-display font-semibold text-3xl sm:text-4xl uppercase tracking-tight flex items-center gap-2">
          <Images className="text-brand" size={30} /> Before &amp; After
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Upload two photos, pick a layout and download a ready-to-post image for Instagram, Facebook and more. Try the draggable slider for a punchy reveal.</p>
      </header>
      <BAModeNav />

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        {/* Preview */}
        <div className="tm-card p-4 flex flex-col items-center justify-center bg-slate-100">
          <canvas ref={canvasRef} data-testid="ba-canvas"
            onPointerDown={onCanvasDown} onPointerMove={onCanvasMove} onPointerUp={stopDrag} onPointerLeave={stopDrag}
            className={`max-w-full max-h-[70vh] shadow-md border border-slate-200 ${layout === 'slider' ? 'cursor-ew-resize touch-none' : ''}`} />
          {layout === 'slider' && <p className="mt-3 text-xs uppercase tracking-widest text-slate-500 font-semibold">Drag the preview to set the reveal</p>}
        </div>

        {/* Controls */}
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <label className="tm-card p-4 text-center cursor-pointer hover:shadow-lg transition-shadow" data-testid="ba-before-drop">
              <Upload className="mx-auto text-brand" size={22} />
              <p className="font-bold mt-1">Before</p>
              <p className="text-[11px] text-slate-500">{beforeBmp ? 'Change photo' : 'Tap to upload'}</p>
              <input type="file" accept="image/*" className="hidden" onChange={onPick('before')} data-testid="ba-before-input" />
            </label>
            <label className="tm-card p-4 text-center cursor-pointer hover:shadow-lg transition-shadow" data-testid="ba-after-drop">
              <Upload className="mx-auto text-brand" size={22} />
              <p className="font-bold mt-1">After</p>
              <p className="text-[11px] text-slate-500">{afterBmp ? 'Change photo' : 'Tap to upload'}</p>
              <input type="file" accept="image/*" className="hidden" onChange={onPick('after')} data-testid="ba-after-input" />
            </label>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-semibold">Layout</p>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setLayout('side')} data-testid="ba-layout-side" className={`inline-flex flex-col items-center justify-center gap-1 border py-2 text-[11px] font-mono uppercase ${layout === 'side' ? 'bg-brand text-white border-brand' : 'border-slate-300 text-slate-600'}`}><Columns size={16} /> Side</button>
              <button onClick={() => setLayout('stacked')} data-testid="ba-layout-stacked" className={`inline-flex flex-col items-center justify-center gap-1 border py-2 text-[11px] font-mono uppercase ${layout === 'stacked' ? 'bg-brand text-white border-brand' : 'border-slate-300 text-slate-600'}`}><Rows size={16} /> Stacked</button>
              <button onClick={() => setLayout('slider')} data-testid="ba-layout-slider" className={`inline-flex flex-col items-center justify-center gap-1 border py-2 text-[11px] font-mono uppercase ${layout === 'slider' ? 'bg-brand text-white border-brand' : 'border-slate-300 text-slate-600'}`}><SlidersHorizontal size={16} /> Slider</button>
            </div>
            {layout === 'slider' && <p className="text-[11px] text-slate-400 mt-1">Slider works best when both photos are taken from the same spot.</p>}
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-semibold">Format</p>
            <div className="grid grid-cols-2 gap-2">
              {PRESETS.map((p) => (
                <button key={p.key} onClick={() => setPresetKey(p.key)} data-testid={`ba-format-${p.key}`}
                  className={`border py-2 px-2 text-left ${presetKey === p.key ? 'border-slate-200 bg-slate-50' : 'border-slate-200'}`}>
                  <span className="block text-sm font-bold">{p.label}</span>
                  <span className="block text-[10px] font-mono text-slate-500">{p.hint}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-semibold">Brand colour</p>
            <div className="flex items-center gap-2 flex-wrap" data-testid="ba-brand-swatches">
              {BRAND_SWATCHES.map((c) => (
                <button key={c} onClick={() => setBrand(c)} data-testid={`ba-brand-${c.replace('#', '')}`} title={c}
                  className={`w-7 h-7 rounded-full border hover:scale-110 transition-transform ${brand === c ? 'ring-2 ring-offset-1 ring-slate-900' : 'border-white'}`} style={{ backgroundColor: c }} />
              ))}
              <input type="color" value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="ba-brand-custom" className="w-8 h-8 border border-slate-300 cursor-pointer bg-white" title="Custom colour" />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Toggle on={showLabels} set={setShowLabels} tid="ba-toggle-labels">Labels</Toggle>
            <Toggle on={showFooter} set={setShowFooter} tid="ba-toggle-footer">Business name</Toggle>
            <Toggle on={showLogo} set={setShowLogo} tid="ba-toggle-logo" disabled={!logoBmp}>Logo</Toggle>
          </div>
          {!logoBmp && <p className="text-[11px] text-slate-400 -mt-2">No logo found — add one under <a href="/settings" className="text-brand underline">Settings</a> to use it here.</p>}

          {showLogo && logoBmp && (
            <div className="space-y-2" data-testid="ba-logo-controls">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">Logo position</p>
                <div className="grid grid-cols-5 gap-1">
                  {[['footer', 'Footer'], ['tl', 'Top L'], ['tr', 'Top R'], ['bl', 'Bot L'], ['br', 'Bot R']].map(([v, l]) => (
                    <button key={v} onClick={() => setLogoPos(v)} data-testid={`ba-logopos-${v}`}
                      className={`border py-1.5 text-[10px] font-mono uppercase ${logoPos === v ? 'bg-slate-900 text-white border-slate-800' : 'border-slate-300 text-slate-600'}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">Logo size</p>
                <div className="grid grid-cols-3 gap-1">
                  {[['s', 'Small'], ['m', 'Medium'], ['l', 'Large']].map(([v, l]) => (
                    <button key={v} onClick={() => setLogoSize(v)} data-testid={`ba-logosize-${v}`}
                      className={`border py-1.5 text-[10px] font-mono uppercase ${logoSize === v ? 'bg-slate-900 text-white border-slate-800' : 'border-slate-300 text-slate-600'}`}>{l}</button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none border border-slate-200 px-3 py-2">
                <input data-testid="ba-logo-transparent" type="checkbox" checked={logoTransparent} onChange={(e) => setLogoTransparent(e.target.checked)} className="w-4 h-4 accent-brand" />
                <span className="text-[11px] uppercase tracking-wider text-slate-700 font-semibold">Transparent logo (remove white)</span>
              </label>
              {logoTransparent && (
                <div data-testid="ba-logo-threshold-panel">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">White removal strength</p>
                    <span className="font-mono text-[11px] text-slate-600" data-testid="ba-logo-threshold-value">{logoThreshold}</span>
                  </div>
                  <input data-testid="ba-logo-threshold" type="range" min={180} max={255} step={1}
                    value={logoThreshold} onChange={(e) => setLogoThreshold(parseInt(e.target.value))}
                    className="w-full accent-brand" />
                  <div className="flex justify-between text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                    <span>← Aggressive</span><span>Pure white →</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {showFooter && (
            <input value={footerText} onChange={(e) => setFooterText(e.target.value)} placeholder="Your business name"
              data-testid="ba-footer-text" className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-brand outline-none" />
          )}

          <div className="flex gap-2">
            <button onClick={download} data-testid="ba-download-btn" className="tm-btn flex-1 justify-center inline-flex"><Download size={16} /> Download image</button>
            <button onClick={() => { setBeforeBmp(null); setAfterBmp(null); }} data-testid="ba-reset-btn" title="Clear photos"
              className="border border-slate-800 px-3 hover:bg-slate-900 hover:text-white transition-colors"><RefreshCw size={16} /></button>
          </div>
          <button onClick={saveToGallery} disabled={savingGallery} data-testid="ba-save-gallery-btn"
            className="w-full justify-center inline-flex items-center gap-2 border border-slate-800 py-2.5 text-sm uppercase tracking-wide hover:bg-slate-900 hover:text-white transition-colors disabled:opacity-60 font-semibold">
            {savingGallery ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : <><Save size={16} /> Save to gallery</>}
          </button>
          <p className="text-[11px] text-slate-400">Downloads are created on your device. "Save to gallery" keeps a copy in your account (<a href="/gallery" className="text-brand underline">view gallery</a>).</p>
        </div>
      </div>

      {/* Caption helper */}
      <div className="mt-8 tm-card p-5 border border-slate-200" data-testid="ba-caption">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={20} className="text-brand" />
          <h2 className="font-display font-bold text-lg uppercase tracking-tight">Caption helper</h2>
        </div>
        <p className="text-slate-600 text-sm mb-4">Get a ready-to-paste Instagram / Facebook caption and hashtags for this job.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={capTrade} onChange={(e) => setCapTrade(e.target.value)} placeholder="Your trade (e.g. Landscaper, Plasterer)"
            data-testid="ba-caption-trade" className="border border-slate-300 px-3 py-2 text-sm focus:border-brand outline-none" />
          <input value={capDesc} onChange={(e) => setCapDesc(e.target.value)} placeholder="The job (e.g. new sandstone patio in Aylesbury)"
            data-testid="ba-caption-desc" className="border border-slate-300 px-3 py-2 text-sm focus:border-brand outline-none" />
        </div>
        <button onClick={genCaption} disabled={capLoading} data-testid="ba-caption-generate" className="tm-btn inline-flex mt-3 disabled:opacity-60">
          {capLoading ? <><Loader2 size={16} className="animate-spin" /> Writing…</> : <><Sparkles size={16} /> Generate caption</>}
        </button>

        {(caption || hashtags.length > 0) && (
          <div className="mt-4 space-y-3" data-testid="ba-caption-result">
            <div className="tm-card p-3 bg-slate-50">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Caption</span>
                <button onClick={() => copy(caption, 'cap')} data-testid="ba-caption-copy" className="inline-flex items-center gap-1 text-xs uppercase hover:text-brand font-semibold">{copied === 'cap' ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}</button>
              </div>
              <p className="text-sm whitespace-pre-wrap text-slate-800">{caption}</p>
            </div>
            {hashtags.length > 0 && (
              <div className="tm-card p-3 bg-slate-50">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Hashtags</span>
                  <button onClick={() => copy(hashtags.join(' '), 'tags')} data-testid="ba-hashtags-copy" className="inline-flex items-center gap-1 text-xs uppercase hover:text-brand font-semibold">{copied === 'tags' ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}</button>
                </div>
                <p className="text-sm text-blue-700 break-words">{hashtags.join(' ')}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
